














































































































import { Component, Prop, Watch, Vue } from "vue-property-decorator";

import { endpoints } from "@/endpoints";
import { eventLoading } from "@/components/sce/EventLoading";
import { buildRcaUrl, RcaFilters } from "./filterQueryParams";
import { fmtFloat } from "./formatters";

const mapboxgl = require("mapbox-gl");

/** Aggregated ~10 km grid cells drive the colour field; link geometry draws the red lines
 * and link centroids the dots — separate sources for each. */
const CELLS_SOURCE_ID = "rca-hotspot-cells";
const LINK_LINES_SOURCE_ID = "rca-hotspot-link-lines";
const LINKS_SOURCE_ID = "rca-hotspot-links";
const HIGHLIGHT_SOURCE_ID = "rca-hotspot-highlight";
const HIGHLIGHT_LINE_SOURCE_ID = "rca-hotspot-highlight-line";
const CELLS_FILL_LAYER_ID = "rca-hotspot-cells-fill";
const LINK_LINES_LAYER_ID = "rca-hotspot-link-lines-layer";
const POINTS_LAYER_ID = "rca-hotspot-points";
const HIGHLIGHT_LAYER_ID = "rca-hotspot-highlight-layer";
/** Wide, semi-transparent corridor highlight line. */
const HIGHLIGHT_LINE_GLOW_LAYER_ID = "rca-hotspot-highlight-line-glow";

/** Individual links (red lines + dots) only render at/above this zoom */
const LINK_DETAIL_MIN_ZOOM = 8;

/** Cell colour ramp — yellow (low intensity) to burgundy (high). Kept in sync with the
 * .legend-bar gradient in the stylesheet. */
const CELL_RAMP = [
    "#FFE14D",
    "#FDB833",
    "#F2832E",
    "#E14B26",
    "#C1121F",
    "#7B0D2B",
];

interface MapMetric {
    /** Cell property key returned in the hotspot payload. */
    key: string;
    /** Dropdown + legend label. */
    label: string;
    /** Legend caption describing how the cell value is built. */
    legendNote: string;
    /** Label and body for the "?" tooltip. */
    helpLabel: string;
    helpBody: string;
    format: (v: number) => string;
}

/** Switchable metrics that colour the hotspot grid. New metrics: add the cell
 * value in the backend payload, then add an entry here. Labels/help also live in
 * metrics_catalog.json (Definitions tab). */
const MAP_METRICS: MapMetric[] = [
    {
        key: "intensity",
        label: "Closure intensity",
        legendNote: "Per ~10 km cell: road length × days closed",
        helpLabel: "Closure-type metric:",
        helpBody: "No modelled freight movements are involved.",
        // This is road length multiplied by days closed — the same km-days unit the
        // Hotspots and Regions tabs show. Naming it stops the number looking like a score.
        format: (v: number) => `${Math.round(v).toLocaleString()} km-days`,
    },
    {
        key: "abs_trailer_delta",
        label: "Change in trailers vs baseline",
        legendNote:
            "Per ~10 km cell:\nchange in trailers (baseline vs scenario)",
        helpLabel: "Exposure-type metric:",
        helpBody:
            "Reflects modelled freight movements relative to the baseline.",
        format: (v: number) => Math.round(v).toLocaleString(),
    },
];

@Component
export default class LinkHotspotsMap extends Vue {
    @Prop({ required: true }) startDate!: string;
    @Prop({ required: true }) endDate!: string;
    /** Global sidebar filters — narrow the hotspot links to matching trips. */
    @Prop({ default: () => ({}) }) filters!: RcaFilters;
    /** Whether the Hotspots tab is the active one. Mapbox-GL sizes itself to the
     * container's bounding rect, so a map mounted while the tab is hidden ends up 0×0;
     * we call map.resize() whenever this becomes true. */
    @Prop({ default: true }) active!: boolean;

    /** Per-link records — centroids, metrics and line geometry — for the dots, the red
     * link lines and the table row-zoom. */
    links: any[] = [];
    /** Aggregated grid cells: { bounds: [w, s, e, n], intensity, closed_km }. */
    cells: any[] = [];
    loading = false;
    error: string | null = null;

    /** What the map is currently focused on — drives the focus badge at the top of the map.
     * Set by focusLink (a segment) / focusRoad (a corridor), cleared when data reloads. */
    focusKind: "segment" | "road" | null = null;
    focusLinkId: number | null = null;
    focusRoadName: string | null = null;

    /** Which metric colours the hotspot grid (switchable via the title dropdown). */
    metricOptions = MAP_METRICS;
    selectedMetric = "intensity";

    get currentMetric(): MapMetric {
        return (
            MAP_METRICS.find((m) => m.key === this.selectedMetric) ??
            MAP_METRICS[0]
        );
    }

    @Watch("selectedMetric")
    onMetricChanged() {
        // Recolour the existing cells — the data is already present, no refetch needed.
        if (!this.map || !this.mapLoaded) return;
        this.map.setPaintProperty(
            CELLS_FILL_LAYER_ID,
            "fill-color",
            this.cellColorExpression(),
        );
    }

    // mapbox-gl plumbing
    mapboxAccessToken =
        "pk.eyJ1IjoiYm9uMTMyIiwiYSI6ImNqdXRhYmw1OTA1eGUzeW5yZGo3OWZmankifQ.RYbaSeGdz3Nq_hIGWXrpSw";
    mapStyleUrl = "mapbox://styles/bon132/cmm8gavux000101sjauuwfvpe";
    /** Australia-wide fallback view; renderData() fitBounds once link data arrives. */
    initialCenter: [number, number] = [134, -28];
    initialZoom = 3.8;

    private map: any = null;
    private popup: any = null;
    private resizeObserver: any = null;
    private mapLoaded = false;

    mounted() {
        this.fetch();
        this.initMap();
    }

    beforeDestroy() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }
        if (this.popup) {
            this.popup.remove();
            this.popup = null;
        }
        if (this.map) {
            this.map.remove();
            this.map = null;
        }
    }

    /** One key over every fetch input so a param change triggers exactly one fetch. */
    private get fetchParamsKey(): string {
        return `${this.startDate}|${this.endDate}|${JSON.stringify(
            this.filters,
        )}`;
    }

    @Watch("fetchParamsKey")
    onParamsChanged() {
        this.fetch();
    }

    @Watch("active")
    onActiveChanged(now: boolean) {
        if (!now || !this.map) return;
        // Two-step resize after activation — same rationale as GeographyTab: let the
        // v-tab-item finish its display:none -> block transition before mapbox re-measures.
        requestAnimationFrame(() => {
            window.setTimeout(() => {
                if (this.map) this.map.resize();
            }, 80);
        });
    }

    fetch() {
        if (!this.startDate || !this.endDate) return;
        this.loading = true;
        this.error = null;
        eventLoading.startLoading();
        Vue.axios({
            url: buildRcaUrl(
                endpoints.roadClosureAnalyticsLinkHotspotPointsUrl,
                { start_date: this.startDate, end_date: this.endDate },
                this.filters,
            ),
        })
            .then((response: any) => {
                this.links = response.data?.links || [];
                this.cells = response.data?.cells || [];
                this.renderData();
            })
            .catch((err: any) => {
                console.error("[RCA HotspotsMap]", err);
                this.error = err?.message || "Failed to load hotspot data.";
                this.links = [];
                this.cells = [];
            })
            .finally(() => {
                this.loading = false;
                eventLoading.stopLoading();
            });
    }

    async initMap() {
        await this.$nextTick();
        const container = this.$refs.mapContainer as HTMLElement | undefined;
        if (!container) return;
        try {
            mapboxgl.accessToken = this.mapboxAccessToken;
            this.map = new mapboxgl.Map({
                container,
                style: this.mapStyleUrl,
                center: this.initialCenter,
                zoom: this.initialZoom,
                attributionControl: false,
            });
            this.map.addControl(new mapboxgl.NavigationControl(), "top-right");
            this.map.on("load", () => {
                this.addLayers();
                this.mapLoaded = true;
                this.renderData();
                if (this.map) this.map.resize();
            });
            const ROCtor = (window as any).ResizeObserver;
            if (typeof ROCtor !== "undefined") {
                this.resizeObserver = new ROCtor(() => {
                    if (this.map) this.map.resize();
                });
                this.resizeObserver.observe(container);
            }
        } catch (err) {
            const e = err as any;
            console.error("[RCA HotspotsMap mapbox-gl]", e);
            this.error = e?.message || "Could not initialise map.";
        }
    }

    /** Add the (initially empty) GeoJSON sources plus the layers, bottom to top: grid fill,
     * red closed-link lines, link dots, highlight ring. Lines + dots only render zoomed in. */
    addLayers() {
        const empty = { type: "FeatureCollection", features: [] };
        this.map.addSource(CELLS_SOURCE_ID, { type: "geojson", data: empty });
        this.map.addSource(LINK_LINES_SOURCE_ID, {
            type: "geojson",
            data: empty,
        });
        this.map.addSource(LINKS_SOURCE_ID, { type: "geojson", data: empty });
        this.map.addSource(HIGHLIGHT_SOURCE_ID, {
            type: "geojson",
            data: empty,
        });
        this.map.addSource(HIGHLIGHT_LINE_SOURCE_ID, {
            type: "geojson",
            data: empty,
        });

        this.map.addLayer(
            {
                id: CELLS_FILL_LAYER_ID,
                type: "fill",
                source: CELLS_SOURCE_ID,
                paint: {
                    // fill-color is re-set per render once the intensity range is known.
                    "fill-color": CELL_RAMP[2],
                    "fill-opacity": 0.68,
                },
            },
            this.firstLabelLayerId(),
        );
        // Corridor highlight — a wide, semi-transparent line drawn beneath the grid-cell
        // fill.
        this.map.addLayer(
            {
                id: HIGHLIGHT_LINE_GLOW_LAYER_ID,
                type: "line",
                source: HIGHLIGHT_LINE_SOURCE_ID,
                layout: { "line-cap": "round", "line-join": "round" },
                paint: {
                    "line-color": "#2196f3",
                    "line-opacity": 0.5,
                    "line-blur": 0,
                    "line-width": [
                        "interpolate",
                        ["linear"],
                        ["zoom"],
                        4,
                        24,
                        8,
                        80,
                        12,
                        300,
                        16,
                        600,
                    ],
                },
            },
            CELLS_FILL_LAYER_ID,
        );
        this.map.addLayer(
            {
                id: LINK_LINES_LAYER_ID,
                type: "line",
                source: LINK_LINES_SOURCE_ID,
                minzoom: LINK_DETAIL_MIN_ZOOM,
                layout: { "line-cap": "round", "line-join": "round" },
                paint: {
                    "line-color": "#d62828",
                    "line-width": [
                        "interpolate",
                        ["linear"],
                        ["zoom"],
                        8,
                        1.5,
                        12,
                        3,
                        16,
                        5,
                    ],
                    "line-opacity": 0.9,
                },
            },
            this.firstLabelLayerId(),
        );
        this.map.addLayer(
            {
                id: POINTS_LAYER_ID,
                type: "circle",
                source: LINKS_SOURCE_ID,
                minzoom: LINK_DETAIL_MIN_ZOOM,
                paint: {
                    "circle-radius": [
                        "interpolate",
                        ["linear"],
                        ["zoom"],
                        8,
                        3,
                        13,
                        4.5,
                    ],
                    "circle-color": "#0b3a66",
                    "circle-opacity": 0.75,
                    "circle-stroke-width": 0.6,
                    "circle-stroke-color": "rgba(255,255,255,0.9)",
                },
            },
            this.firstLabelLayerId(),
        );
        this.map.addLayer({
            id: HIGHLIGHT_LAYER_ID,
            type: "circle",
            source: HIGHLIGHT_SOURCE_ID,
            paint: {
                "circle-radius": 13,
                "circle-color": "rgba(0,0,0,0)",
                "circle-stroke-width": 3,
                "circle-stroke-color": "#013e75",
            },
        });

        this.attachHoverHandlers();
    }

    /**
     * Returns the layer ID used as the insertion point for overlays.
     *
     * Overlays are added below map labels so road and place names remain visible,
     * but above the road and rail network layers. Falls back to the first symbol
     * layer if no text-label layer is found.
     */
    firstLabelLayerId(): string | undefined {
        const layers = this.map?.getStyle()?.layers ?? [];
        const firstLabel = layers.find(
            (l: any) =>
                l.type === "symbol" && l.layout && l.layout["text-field"],
        );
        if (firstLabel) return firstLabel.id;
        const firstSymbol = layers.find((l: any) => l.type === "symbol");
        return firstSymbol?.id;
    }

    /** A Mapbox ``interpolate`` expression mapping the selected cell metric to the colour
     * ramp. The upper bound is the 95th-percentile value, so a few extreme cells clamp to
     * red instead of flattening every other cell into the cool end of the ramp. */
    cellColorExpression(): any {
        const ramp = CELL_RAMP;
        const metric = this.selectedMetric;
        const sorted = this.cells
            .map((c) => c[metric])
            .filter((v) => v > 0)
            .sort((a, b) => a - b);
        let top = 1;
        if (sorted.length) {
            top =
                sorted[Math.floor(0.95 * (sorted.length - 1))] ||
                sorted[sorted.length - 1] ||
                1;
        }
        if (!(top > 0)) top = 1;
        return [
            "interpolate",
            ["linear"],
            // Coalesce so a missing/null metric renders as the low end of the ramp rather
            // than Mapbox's default black fill (which happens when the expression errors).
            ["coalesce", ["to-number", ["get", metric]], 0],
            0,
            ramp[0],
            0.25 * top,
            ramp[1],
            0.5 * top,
            ramp[2],
            0.7 * top,
            ramp[3],
            0.85 * top,
            ramp[4],
            top,
            ramp[5],
        ];
    }

    /** Push the current data into the map: grid cells, red closed-link lines, link-centroid
     * dots; recolour the grid. The viewport is left at the fixed Australia-wide view
     * (matching the Geography tab) rather than fitting to the data. */
    renderData() {
        if (!this.map || !this.mapLoaded) return;

        // Grid cells -> rectangle polygons.
        const cellFeatures = this.cells.map((c) => {
            const [w, s, e, n] = c.bounds;
            return {
                type: "Feature",
                geometry: {
                    type: "Polygon",
                    coordinates: [
                        [
                            [w, s],
                            [e, s],
                            [e, n],
                            [w, n],
                            [w, s],
                        ],
                    ],
                },
                properties: {
                    intensity: c.intensity,
                    closed_km: c.closed_km,
                    abs_trailer_delta: c.abs_trailer_delta,
                },
            };
        });

        // Closed-link line geometry + link-centroid dots.
        const lineFeatures: any[] = [];
        const dotFeatures: any[] = [];
        for (const l of this.links) {
            if (l.geometry_geojson) {
                let geom: any = null;
                try {
                    geom = JSON.parse(l.geometry_geojson);
                } catch (e) {
                    geom = null;
                }
                if (
                    geom &&
                    (geom.type === "LineString" ||
                        geom.type === "MultiLineString")
                ) {
                    lineFeatures.push({
                        type: "Feature",
                        geometry: geom,
                        properties: {},
                    });
                }
            }
            if (l.lon == null || l.lat == null) continue;
            dotFeatures.push({
                type: "Feature",
                geometry: { type: "Point", coordinates: [l.lon, l.lat] },
                properties: {
                    link_id: l.link_id,
                    road_name: l.road_name,
                    active_day_count: l.active_day_count || 0,
                    closure_event_count_sum: l.closure_event_count_sum || 0,
                    closed_length_km_sum: l.closed_length_km_sum || 0,
                    max_consecutive_days_closed:
                        l.max_consecutive_days_closed || 0,
                    closure_category_names_label:
                        l.closure_category_names_label || "",
                    state_names_label: l.state_names_label || "",
                    lga_names_label: l.lga_names_label || "",
                },
            });
        }

        (this.map.getSource(CELLS_SOURCE_ID) as any).setData({
            type: "FeatureCollection",
            features: cellFeatures,
        });
        (this.map.getSource(LINK_LINES_SOURCE_ID) as any).setData({
            type: "FeatureCollection",
            features: lineFeatures,
        });
        (this.map.getSource(LINKS_SOURCE_ID) as any).setData({
            type: "FeatureCollection",
            features: dotFeatures,
        });
        (this.map.getSource(HIGHLIGHT_SOURCE_ID) as any).setData({
            type: "FeatureCollection",
            features: [],
        });
        (this.map.getSource(HIGHLIGHT_LINE_SOURCE_ID) as any).setData({
            type: "FeatureCollection",
            features: [],
        });
        // Highlights were just cleared, so clear the focus badge too.
        this.focusKind = null;
        this.focusLinkId = null;
        this.focusRoadName = null;

        this.map.setPaintProperty(
            CELLS_FILL_LAYER_ID,
            "fill-color",
            this.cellColorExpression(),
        );
    }

    /** One map-level hover handler — a dot under the cursor wins (link detail), otherwise
     * the grid cell beneath shows its aggregated hotspot value. The red link lines are not
     * queried — the dots already carry the link detail. */
    attachHoverHandlers() {
        this.popup = new mapboxgl.Popup({
            closeButton: false,
            closeOnClick: false,
            offset: 8,
        });
        this.map.on("mousemove", (e: any) => {
            const feats = this.map.queryRenderedFeatures(e.point, {
                layers: [POINTS_LAYER_ID, CELLS_FILL_LAYER_ID],
            });
            if (!feats.length) {
                this.map.getCanvas().style.cursor = "";
                this.popup.remove();
                return;
            }
            this.map.getCanvas().style.cursor = "pointer";
            const dot = feats.find((f: any) => f.layer.id === POINTS_LAYER_ID);
            if (dot) {
                const p = dot.properties;
                const km = fmtFloat(p.closed_length_km_sum);
                this.popup
                    .setLngLat(dot.geometry.coordinates)
                    .setHTML(
                        `<div class="rca-hotspot-popup">` +
                            `<div class="popup-name">${
                                p.road_name || "(unnamed road)"
                            }</div>` +
                            `<div class="popup-sub">Road segment ${p.link_id}</div>` +
                            `<div><span class="popup-k">Closed-segment km-days:</span> ` +
                            `<span class="popup-v">${km} km-days</span></div>` +
                            `<div><span class="popup-k">Closure duration (days):</span> ` +
                            `<span class="popup-v">${p.active_day_count}</span></div>` +
                            `<div><span class="popup-k">Closure events:</span> ` +
                            `<span class="popup-v">${p.closure_event_count_sum}</span></div>` +
                            `<div><span class="popup-k">Max consecutive days closed:</span> ` +
                            `<span class="popup-v">${
                                p.max_consecutive_days_closed || 0
                            }</span></div>` +
                            `<div><span class="popup-k">Closure categories:</span> ` +
                            `<span class="popup-v">${
                                p.closure_category_names_label || ""
                            }</span></div>` +
                            `<div><span class="popup-k">States:</span> ` +
                            `<span class="popup-v">${
                                p.state_names_label || ""
                            }</span></div>` +
                            `<div><span class="popup-k">LGAs:</span> ` +
                            `<span class="popup-v">${
                                p.lga_names_label || ""
                            }</span></div>` +
                            `</div>`,
                    )
                    .addTo(this.map);
            } else {
                const p = feats[0].properties;
                const m = this.currentMetric;
                this.popup
                    .setLngLat(e.lngLat)
                    .setHTML(
                        `<div class="rca-hotspot-popup">` +
                            `<div class="popup-name">Hotspot cell (~10 km)</div>` +
                            `<div><span class="popup-k">Closed road within the cell:</span> ` +
                            `<span class="popup-v">${fmtFloat(
                                p.closed_km,
                            )} km</span></div>` +
                            `<div><span class="popup-k">${m.label}:</span> ` +
                            `<span class="popup-v">${m.format(
                                Number(p[m.key]) || 0,
                            )}</span></div>` +
                            `</div>`,
                    )
                    .addTo(this.map);
            }
        });
        this.map.on("mouseout", () => {
            this.map.getCanvas().style.cursor = "";
            if (this.popup) this.popup.remove();
        });
        // Click a dot to drill that closed segment into the range-exposure panel below the map.
        this.map.on("click", POINTS_LAYER_ID, (e: any) => {
            const feat = e.features && e.features[0];
            if (!feat) return;
            const p = feat.properties;
            const linkId = Number(p.link_id);
            const roadName = String(p.road_name || "");

            // Update the focus badge + highlight ring straight from the clicked feature.
            this.focusKind = "segment";
            this.focusLinkId = linkId;
            this.focusRoadName = roadName || null;
            const ring = this.map.getSource(HIGHLIGHT_SOURCE_ID) as any;
            if (ring) {
                ring.setData({
                    type: "FeatureCollection",
                    features: [
                        {
                            type: "Feature",
                            geometry: feat.geometry,
                            properties: {},
                        },
                    ],
                });
            }
            const glow = this.map.getSource(HIGHLIGHT_LINE_SOURCE_ID) as any;
            if (glow) glow.setData({ type: "FeatureCollection", features: [] });

            this.$emit("select-segment", {
                link_id: linkId,
                road_name: roadName,
            });
        });
    }

    /** Text for the focus badge at the top of the map. Segment focus shows the segment id
     * and its road name; corridor focus shows the road name. */
    get focusLabel(): string | null {
        if (this.focusKind === "segment") {
            return `Road segment ${this.focusLinkId} · ${
                this.focusRoadName || "(unnamed road)"
            }`;
        }
        if (this.focusKind === "road") {
            return this.focusRoadName || "(unnamed road)";
        }
        return null;
    }

    /** Clear the current focus — hides the badge, removes the map highlight (ring/glow), and
     * zooms back out to the full (Australia-wide) extent. */
    clearFocus() {
        this.focusKind = null;
        this.focusLinkId = null;
        this.focusRoadName = null;
        if (!this.map || !this.mapLoaded) return;
        const ring = this.map.getSource(HIGHLIGHT_SOURCE_ID) as any;
        if (ring) ring.setData({ type: "FeatureCollection", features: [] });
        const glow = this.map.getSource(HIGHLIGHT_LINE_SOURCE_ID) as any;
        if (glow) glow.setData({ type: "FeatureCollection", features: [] });
        this.map.flyTo({
            center: this.initialCenter,
            zoom: this.initialZoom,
            duration: 600,
        });
    }

    /**
     * Called by HotspotsTab when the user clicks a row in the Closed link
     * hotspots table. Flies to the link and drops a highlight ring on it.
     */
    focusLink(linkId: number) {
        if (!this.map || !this.mapLoaded) return;
        const link = this.links.find((l) => l.link_id === linkId);
        if (!link || link.lon == null || link.lat == null) return;

        this.focusKind = "segment";
        this.focusLinkId = linkId;
        this.focusRoadName = link.road_name || null;

        const highlight = this.map.getSource(HIGHLIGHT_SOURCE_ID) as any;
        if (highlight) {
            highlight.setData({
                type: "FeatureCollection",
                features: [
                    {
                        type: "Feature",
                        geometry: {
                            type: "Point",
                            coordinates: [link.lon, link.lat],
                        },
                        properties: {},
                    },
                ],
            });
        }
        // A single segment uses the ring, not the corridor glow — clear any prior glow.
        const glow = this.map.getSource(HIGHLIGHT_LINE_SOURCE_ID) as any;
        if (glow) glow.setData({ type: "FeatureCollection", features: [] });

        this.map.flyTo({
            center: [link.lon, link.lat],
            zoom: 12,
            duration: 600,
        });
    }

    /**
     * Called by HotspotsTab when the user clicks a corridor bar in the
     * "highest closure activity" chart. Falls back to a single flyTo when the corridor has just one segment.
     */
    focusRoad(roadName: string) {
        if (!this.map || !this.mapLoaded || !roadName) return;
        const matches = this.links.filter(
            (l) => l.road_name === roadName && l.lon != null && l.lat != null,
        );
        if (!matches.length) return;

        this.focusKind = "road";
        this.focusRoadName = roadName;
        this.focusLinkId = null;

        // Highlight the corridor by drawing the wide semi-transparent line along its
        // segments' geometry (it reads as a band around the corridor's cells).
        const lineFeatures: any[] = [];
        for (const l of matches) {
            if (!l.geometry_geojson) continue;
            let geom: any = null;
            try {
                geom = JSON.parse(l.geometry_geojson);
            } catch (e) {
                geom = null;
            }
            if (
                geom &&
                (geom.type === "LineString" || geom.type === "MultiLineString")
            ) {
                lineFeatures.push({
                    type: "Feature",
                    geometry: geom,
                    properties: {},
                });
            }
        }

        const ring = this.map.getSource(HIGHLIGHT_SOURCE_ID) as any;
        const glow = this.map.getSource(HIGHLIGHT_LINE_SOURCE_ID) as any;
        if (lineFeatures.length) {
            // Glow replaces the centroid rings (which overlap on a corridor).
            if (glow)
                glow.setData({
                    type: "FeatureCollection",
                    features: lineFeatures,
                });
            if (ring) ring.setData({ type: "FeatureCollection", features: [] });
        } else {
            // No segment geometry available — fall back to centroid rings.
            if (ring)
                ring.setData({
                    type: "FeatureCollection",
                    features: matches.map((l) => ({
                        type: "Feature",
                        geometry: {
                            type: "Point",
                            coordinates: [l.lon, l.lat],
                        },
                        properties: {},
                    })),
                });
            if (glow) glow.setData({ type: "FeatureCollection", features: [] });
        }

        if (matches.length === 1) {
            this.map.flyTo({
                center: [matches[0].lon, matches[0].lat],
                zoom: 12,
                duration: 600,
            });
            return;
        }

        const bounds = new mapboxgl.LngLatBounds();
        for (const l of matches) {
            bounds.extend([l.lon, l.lat]);
        }
        this.map.fitBounds(bounds, {
            padding: 80,
            maxZoom: 12,
            duration: 600,
        });
    }
}
